<template>
    <div>
        <div class="text-center mb-6">
            <v-icon size="48" color="primary" class="mb-2">{{ headerIcon }}</v-icon>
            <h3 class="text-h6 font-weight-bold">Set Up Your Asset</h3>
            <p class="text-body-2 text-grey-darken-1">Name what you're tracking and link it to your device</p>
        </div>

        <div v-if="loadingAssets" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary"/>
        </div>

        <template v-else>
        <v-alert v-if="errors.length" class="mb-4" type="error" density="compact" variant="tonal" closable>
            <div v-for="error in errors" :key="error">{{ error }}</div>
        </v-alert>

        <!-- Mode toggle (only if unassigned assets exist) -->
        <v-btn-toggle
            v-if="unassignedAssets.length > 0"
            v-model="mode"
            mandatory
            color="primary"
            variant="outlined"
            density="compact"
            divided
            class="mb-5 d-flex"
        >
            <v-btn value="existing" class="flex-grow-1 text-none">
                <v-icon start size="18">mdi-format-list-bulleted</v-icon>
                Use Existing ({{ unassignedAssets.length }})
            </v-btn>
            <v-btn value="create" class="flex-grow-1 text-none">
                <v-icon start size="18">mdi-plus-circle-outline</v-icon>
                Create New
            </v-btn>
        </v-btn-toggle>

        <!-- EXISTING ASSET MODE -->
        <div v-if="mode === 'existing' && unassignedAssets.length > 0">
            <div class="existing-assets-list">
                <v-card
                    v-for="asset in unassignedAssets"
                    :key="asset.id"
                    :class="{'selected-asset': selectedAsset?.id === asset.id}"
                    class="existing-asset-card mb-2 cursor-pointer"
                    :elevation="selectedAsset?.id === asset.id ? 3 : 0"
                    variant="outlined"
                    rounded="lg"
                    @click="selectedAsset = asset"
                >
                    <div class="d-flex align-center pa-3 ga-3">
                        <v-avatar :color="selectedAsset?.id === asset.id ? 'primary' : 'grey-lighten-2'" size="40" rounded="lg">
                            <v-icon :color="selectedAsset?.id === asset.id ? 'white' : 'grey-darken-1'" size="22">
                                {{ getAssetIcon(asset) }}
                            </v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                            <div class="text-body-2 font-weight-medium">{{ asset.label }}</div>
                            <div class="text-caption text-grey">{{ asset.asset_type?.name || 'No type' }}</div>
                        </div>
                        <v-icon v-if="selectedAsset?.id === asset.id" color="primary">mdi-check-circle</v-icon>
                        <v-icon v-else color="grey-lighten-1" size="20">mdi-circle-outline</v-icon>
                    </div>
                </v-card>
            </div>

            <!-- Device info -->
            <div class="info-card mt-4 mb-3">
                <v-icon size="20" color="primary">mdi-access-point</v-icon>
                <div class="flex-grow-1">
                    <div class="text-caption text-grey">Your Device</div>
                    <div class="text-body-2 font-weight-medium">{{ device?.slno }}</div>
                </div>
                <v-icon size="16" color="grey">mdi-lock</v-icon>
            </div>

            <!-- Driver/Operator section (existing asset mode) -->
            <div class="driver-section mb-3" :class="{'driver-section-open': showDriverExisting}">
                <div class="d-flex align-center ga-3 pa-3 cursor-pointer" @click="showDriverExisting = !showDriverExisting">
                    <v-icon size="20" color="primary">mdi-account-outline</v-icon>
                    <div class="flex-grow-1">
                        <div class="text-body-2 font-weight-medium">Assign {{ driverLabel }} (optional)</div>
                        <div class="text-caption text-grey">{{ showDriverExisting ? 'Assign someone to this asset' : `Tap to assign a ${driverLabel}` }}</div>
                    </div>
                    <v-icon color="primary">{{ showDriverExisting ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </div>
                <template v-if="showDriverExisting">
                    <v-divider/>
                    <div class="pa-3">
                        <v-select
                            v-model="selectedDriverId"
                            :items="existingDrivers"
                            :item-title="d => `${d.user_first_name || d.first_name || ''} ${d.user_last_name || d.last_name || ''}`.trim()"
                            item-value="id"
                            :label="`Select ${driverLabel}`"
                            prepend-inner-icon="mdi-account-outline"
                            variant="outlined"
                            density="compact"
                            hide-details
                            clearable
                            no-data-text="No drivers available - create a driver from the Drivers page first"
                        />
                    </div>
                </template>
            </div>

            <v-btn
                color="primary"
                size="large"
                block
                class="mt-4"
                :disabled="!selectedAsset || processing"
                :loading="processing"
                @click="assignToExisting"
            >
                <v-icon start>mdi-link-variant</v-icon>
                Link Device & Finish
            </v-btn>
        </div>

        <!-- CREATE NEW MODE -->
        <v-form v-else v-model="formValid" validate-on="input lazy" @submit.prevent="createAsset">
            <!-- Asset photo -->
            <div class="asset-photo-picker mb-4" @click="$refs.imageInput?.click()">
                <v-avatar size="72" rounded="lg" :color="imagePreview ? undefined : 'grey-lighten-3'">
                    <v-img v-if="imagePreview" :src="imagePreview" cover/>
                    <v-icon v-else size="32" color="grey-darken-1">mdi-camera-plus</v-icon>
                </v-avatar>
                <div class="ml-4">
                    <div class="text-body-2 font-weight-medium">Asset Photo</div>
                    <div class="text-caption text-grey">{{ imagePreview ? 'Click to change' : 'Add a photo (optional)' }}</div>
                </div>
                <v-icon class="ml-auto" color="grey">mdi-chevron-right</v-icon>
            </div>
            <v-file-input ref="imageInput" v-model="image" accept="image/*" class="d-none" @change="onImageChange"/>

            <v-text-field
                v-model="assetName"
                :rules="[v => !!v || 'Asset name is required', v => (!v || v.length <= 64) || 'Name must be 64 characters or less']"
                label="Asset Name *"
                placeholder="e.g. Company Truck #1"
                prepend-inner-icon="mdi-label"
                variant="outlined"
                class="mb-2"
                autofocus
            />

            <v-text-field
                v-if="showVehicleFields"
                v-model="vin"
                label="VIN Number"
                placeholder="Optional - 17 characters"
                prepend-inner-icon="mdi-identifier"
                variant="outlined"
                maxlength="17"
                counter
                clearable
                hint="Vehicle Identification Number"
                class="mb-2"
                @update:modelValue="onVinInput"
            />
            <div v-if="vinLoading" class="d-flex align-center mt-n1 mb-3">
                <v-progress-circular size="14" width="2" indeterminate color="primary" class="mr-2"/>
                <span class="text-caption text-medium-emphasis">Decoding VIN...</span>
            </div>
            <v-card v-else-if="vinDetails && vinHasData" variant="tonal" color="success" density="compact" class="mt-n1 mb-3" rounded="lg">
                <v-card-text class="pa-3 d-flex align-start">
                    <v-icon icon="mdi-car-info" size="20" class="mr-2 mt-1" color="success"/>
                    <div class="flex-grow-1">
                        <div class="text-caption font-weight-bold text-success mb-1">Vehicle Identified</div>
                        <div v-if="vinDetails.manufacturer && vinDetails.manufacturer !== 'Unknown'" class="text-caption"><strong>Make:</strong> {{ vinDetails.manufacturer }}</div>
                        <div v-if="vinDetails.vehicle_type && vinDetails.vehicle_type !== 'Unknown'" class="text-caption"><strong>Body:</strong> {{ vinDetails.vehicle_type }}</div>
                        <div v-if="vinDetails.model_year && vinDetails.model_year !== 'Unknown'" class="text-caption"><strong>Year:</strong> {{ vinDetails.model_year }}</div>
                        <div v-if="vinDetails.country && vinDetails.country !== 'Unknown'" class="text-caption"><strong>Country:</strong> {{ vinDetails.country }}</div>
                    </div>
                    <v-tooltip location="top" :text="vinDetails.check_digit_valid ? 'Valid VIN' : 'Invalid check digit'">
                        <template #activator="{ props: tp }">
                            <v-icon v-bind="tp" :icon="vinDetails.check_digit_valid ? 'mdi-check-circle' : 'mdi-alert-circle'" size="18" :color="vinDetails.check_digit_valid ? 'success' : 'warning'" class="ml-2 mt-1"/>
                        </template>
                    </v-tooltip>
                </v-card-text>
            </v-card>
            <div v-else-if="vinDetails && !vinHasData" class="text-caption text-medium-emphasis mt-n1 mb-3">
                <v-icon icon="mdi-information-outline" size="14" class="mr-1"/>VIN not recognized in our database
            </div>

            <v-text-field
                v-if="showVehicleFields"
                v-model="odometer"
                label="Initial Odometer (miles)"
                placeholder="Optional"
                prepend-inner-icon="mdi-counter"
                variant="outlined"
                type="number"
                class="mb-2"
            />

            <!-- Asset type info -->
            <div class="info-card mb-3">
                <v-icon size="20" color="primary">mdi-shape-outline</v-icon>
                <div class="flex-grow-1">
                    <div class="text-caption text-grey">Asset Type</div>
                    <div class="text-body-2 font-weight-medium">{{ assetType?.name || 'Not selected' }}</div>
                </div>
                <v-btn variant="text" size="small" color="primary" @click="$emit('changeAssetType')">Change</v-btn>
            </div>

            <!-- Device info -->
            <div class="info-card mb-3">
                <v-icon size="20" color="primary">mdi-access-point</v-icon>
                <div class="flex-grow-1">
                    <div class="text-caption text-grey">Tracking Device</div>
                    <div class="text-body-2 font-weight-medium">{{ device?.slno }}</div>
                </div>
                <v-icon size="16" color="grey">mdi-lock</v-icon>
            </div>

            <!-- Driver/Operator section (create mode) -->
            <div class="driver-section mb-3" :class="{'driver-section-open': showDriverCreate}">
                <div class="d-flex align-center ga-3 pa-3 cursor-pointer" @click="showDriverCreate = !showDriverCreate">
                    <v-icon size="20" color="primary">mdi-account-outline</v-icon>
                    <div class="flex-grow-1">
                        <div class="text-body-2 font-weight-medium">Assign {{ driverLabel }} (optional)</div>
                        <div class="text-caption text-grey">{{ showDriverCreate ? 'Assign someone to this asset' : `Tap to assign a ${driverLabel}` }}</div>
                    </div>
                    <v-icon color="primary">{{ showDriverCreate ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </div>
                <template v-if="showDriverCreate">
                    <v-divider/>
                    <div class="pa-3">
                        <v-select
                            v-model="selectedDriverId"
                            :items="existingDrivers"
                            :item-title="d => `${d.user_first_name || d.first_name || ''} ${d.user_last_name || d.last_name || ''}`.trim()"
                            item-value="id"
                            :label="`Select ${driverLabel}`"
                            prepend-inner-icon="mdi-account-outline"
                            variant="outlined"
                            density="compact"
                            hide-details
                            clearable
                            no-data-text="No drivers available - create a driver from the Drivers page first"
                        />
                    </div>
                </template>
            </div>

            <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                class="mt-4"
                :disabled="!formValid || processing"
                :loading="processing"
            >
                <v-icon start>mdi-check-circle</v-icon>
                Create & Start Tracking
            </v-btn>
        </v-form>
        </template>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";

const props = defineProps({
    assetType: {type: Object, default: null},
    device: {type: Object, default: null},
});

const emit = defineEmits(['created', 'changeAssetType']);
const snackbar = useSnackbarStore();
const {user} = useAuthStore();

const mode = ref('create');
const loadingAssets = ref(true);
const unassignedAssets = ref([]);
const selectedAsset = ref(null);
const assetName = ref('');
const vin = ref('');
const odometer = ref('');
const image = ref(null);
const imagePreview = ref(null);
const formValid = ref(false);
const processing = ref(false);
const errors = ref([]);

// VIN lookup state
const vinDetails = ref(null);
const vinLoading = ref(false);
let vinDebounceTimer = null;
let currentVinLookup = null;
const vinHasData = computed(() => {
    if (!vinDetails.value) return false;
    const d = vinDetails.value;
    return (d.manufacturer && d.manufacturer !== 'Unknown') ||
           (d.vehicle_type && d.vehicle_type !== 'Unknown') ||
           (d.model_year && d.model_year !== 'Unknown') ||
           (d.country && d.country !== 'Unknown');
});
const onVinInput = (val) => {
    vinDetails.value = null;
    clearTimeout(vinDebounceTimer);
    const v = (val || '').trim();
    if (v.length !== 17) { vinLoading.value = false; return; }
    vinLoading.value = true;
    currentVinLookup = v;
    vinDebounceTimer = setTimeout(() => {
        send('get', `vin/lookup`, {vin: v})
            .then((response) => {
                if (currentVinLookup !== v) return;
                vinDetails.value = response.data?.data || null;
            })
            .catch(() => {})
            .finally(() => {
                if (currentVinLookup === v) vinLoading.value = false;
            });
    }, 500);
};

// Driver/Operator state
const existingDrivers = ref([]);
const showDriverExisting = ref(false);
const showDriverCreate = ref(false);
const selectedDriverId = ref(null);

const iconMap = {
    vehicle: 'mdi-car',
    trailer: 'mdi-truck-trailer',
    equipment: 'mdi-tools',
    container: 'mdi-package-variant-closed',
    person: 'mdi-account',
    animal: 'mdi-paw',
    asset: 'mdi-map-marker',
};

const getAssetIcon = (asset) => {
    const cat = (asset.asset_type?.category || '').toLowerCase();
    return iconMap[cat] || 'mdi-map-marker';
};

const showVehicleFields = computed(() => {
    const cat = (props.assetType?.category || '').toLowerCase();
    return ['vehicle', 'equipment'].includes(cat);
});

const driverLabel = computed(() => {
    const cat = (props.assetType?.category || '').toLowerCase();
    return ['equipment', 'container'].includes(cat) ? 'Operator' : 'Driver';
});

const headerIcon = computed(() => {
    const cat = (props.assetType?.category || '').toLowerCase();
    return iconMap[cat] || 'mdi-map-marker';
});

// Reset driver state when switching between existing/create asset modes
watch(mode, () => {
    showDriverExisting.value = false;
    showDriverCreate.value = false;
    selectedDriverId.value = null;
});

onBeforeUnmount(() => { clearTimeout(vinDebounceTimer); });

onMounted(async () => {
    if (props.assetType?.name && !assetName.value) {
        const serial = props.device?.slno;
        assetName.value = serial ? `My ${props.assetType.name} (${serial})` : `My ${props.assetType.name}`;
    }
    await Promise.all([loadUnassignedAssets(), loadDrivers()]);
});

const loadUnassignedAssets = async () => {
    try {
        const response = await send('get', 'assets', {per_page: 100});
        const data = response.data?.data;
        const assets = data?.assets?.data || (Array.isArray(data?.data) ? data.data : []) || (Array.isArray(data) ? data : []);
        unassignedAssets.value = assets.filter(a => !a.device_id);
        if (unassignedAssets.value.length > 0) {
            mode.value = 'existing';
        }
    } catch (e) {
        // If fetching fails, just show create form
    } finally {
        loadingAssets.value = false;
    }
};

const loadDrivers = async () => {
    try {
        const response = await send('get', 'companies/drivers?list=true');
        const data = response.data?.data;
        existingDrivers.value = Array.isArray(data) ? data : (data?.data || []);
    } catch (e) {
        // Silent - driver assignment is optional
    }
};

const onImageChange = () => {
    if (image.value) {
        if (image.value.size > 1024 * 1024) {
            snackbar.showSnackbar('File size exceeds 1MB', 'error');
            image.value = null;
            imagePreview.value = null;
            return;
        }
        if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
        imagePreview.value = URL.createObjectURL(image.value);
    }
};

/** Resolve driver ID - returns selected driver id or null (skip) */
const resolveDriverId = (isExistingMode) => {
    const showFlag = isExistingMode ? showDriverExisting.value : showDriverCreate.value;
    if (!showFlag) return null;
    return selectedDriverId.value || null;
};

const assignToExisting = async () => {
    if (!selectedAsset.value) return;
    errors.value = [];
    processing.value = true;

    const driverId = resolveDriverId(true);

    const payload = {
        device_id: props.device?.id,
        company_id: selectedAsset.value.company_id || user.company?.id,
        asset_type_id: selectedAsset.value.asset_type_id,
        label: selectedAsset.value.label,
    };
    if (driverId) payload.driver_id = driverId;

    try {
        const response = await send('put', `assets/${selectedAsset.value.id}`, payload);
        if (response.data.success) {
            snackbar.showSnackbar('Device assigned to asset successfully!');
            emit('created', response.data.data);
        } else {
            errors.value.push(response.data.message || 'Failed to assign device');
        }
    } catch (errorResponse) {
        errors.value.push(errorResponse.response?.data?.message || 'Failed to assign device to asset');
    } finally {
        processing.value = false;
    }
};

const createAsset = async () => {
    errors.value = [];
    processing.value = true;

    const driverId = resolveDriverId(false);

    const formData = {
        label: assetName.value,
        asset_type_id: props.assetType?.id,
        device_id: props.device?.id,
        company_id: user.company?.id,
        status: 'enabled',
    };
    if (vin.value) formData.vin = vin.value;
    if (odometer.value) formData.odometer = odometer.value;
    if (driverId) formData.driver_id = driverId;
    if (image.value) formData.image = image.value;

    try {
        const response = await send('post', 'assets', formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        });
        if (response.data.success) {
            snackbar.showSnackbar('Asset created successfully!');
            emit('created', response.data.data);
        } else {
            errors.value.push(response.data.message || 'Failed to create asset');
        }
    } catch (errorResponse) {
        if (errorResponse.response?.data?.errors) {
            const validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response?.data?.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        if (!errors.value.length) {
            errors.value.push('Unable to create asset. Please check your connection and try again.');
        }
    } finally {
        processing.value = false;
    }
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.existing-assets-list {
    max-height: 240px;
    overflow-y: auto;
}

.existing-asset-card {
    transition: all 0.15s ease;
    border-color: #e0e0e0;
}

.existing-asset-card:hover {
    border-color: rgba(var(--v-theme-primary), 0.4);
    background: rgba(var(--v-theme-primary), 0.02);
}

.selected-asset {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.06) !important;
}

.info-card {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: 12px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
}

.asset-photo-picker {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 12px;
    background: #fafafa;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    transition: border-color 0.15s;
}

.asset-photo-picker:hover {
    border-color: rgba(var(--v-theme-primary), 0.4);
}

.driver-section {
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    transition: border-color 0.2s, background 0.2s;
}

.driver-section-open {
    border-color: rgba(var(--v-theme-primary), 0.3);
    background: rgba(var(--v-theme-primary), 0.02);
}
</style>
